/**
 * Keyboard Shortcut Manager
 * 
 * Main orchestrator for keyboard shortcuts. Handles event listening,
 * shortcut matching, and handler execution.
 */

import { SHORTCUTS } from '../shortcuts/shortcutConfig';
import { 
  toggleNavigation, 
  showHelp, 
  showNavigationSearch,
  createChatAgent,
  createVoiceAgent,
  uploadDocument,
  matchesShortcut
} from '../handlers/shortcutHandlers';
import { createDoubleShiftDetector } from '../utils/doubleKeyDetector';

/**
 * Handler mapping - connects shortcut handler names to actual functions
 */
const HANDLER_MAP = {
  toggleNavigation,
  showHelp,
  showNavigationSearch,
  createChatAgent,
  createVoiceAgent,
  uploadDocument
};

/**
 * Keyboard Shortcut Manager Class
 */
export class KeyboardShortcutManager {
  constructor() {
    this.isActive = false;
    this.eventListener = null;
    this.doubleShiftDetector = createDoubleShiftDetector();
    this.context = {}; // Store context data needed by handlers
    
    // Track modifier key states to handle timing issues
    this.modifierState = {
      ctrlKey: false,
      shiftKey: false,
      altKey: false,
      metaKey: false
    };
    
    // Bind methods to preserve context
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
  }
  
  /**
   * Initialize the shortcut manager with context
   */
  init(context) {
    this.context = context;
    this.setupDoubleShiftDetector();
    this.start();
  }
  
  /**
   * Setup double shift detector
   */
  setupDoubleShiftDetector() {
    this.doubleShiftDetector.setCallback((event) => {
      const { setShowNavigationSearch } = this.context;
      if (setShowNavigationSearch) {
        showNavigationSearch(event, { setShowNavigationSearch });
      }
    });
  }
  
  /**
   * Start listening for keyboard events
   */
  start() {
    if (this.isActive) return;
    
    // Add both keydown and keyup listeners
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
    this.isActive = true;
  }
  
  /**
   * Stop listening for keyboard events
   */
  stop() {
    if (!this.isActive) return;
    
    // Remove all event listeners
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    
    this.isActive = false;
  }
  
  /**
   * Update context data
   */
  updateContext(newContext) {
    this.context = { ...this.context, ...newContext };
  }
  
  /**
   * Handle keyup events to track modifier state
   */
  handleKeyUp(event) {
    // Only update modifier state for actual modifier key releases
    if (event.key === 'Control' || event.key === 'Shift' || event.key === 'Alt' || event.key === 'Meta') {
      this.modifierState.ctrlKey = event.ctrlKey;
      this.modifierState.shiftKey = event.shiftKey;
      this.modifierState.altKey = event.altKey;
      this.modifierState.metaKey = event.metaKey;
    }
  }
  
  /**
   * Main keyboard event handler
   */
  handleKeyDown(event) {
    // Update modifier state on key press - but preserve state when pressing non-modifier keys
    if (event.key === 'Control' || event.key === 'Shift' || event.key === 'Alt' || event.key === 'Meta') {
      this.modifierState.ctrlKey = event.ctrlKey;
      this.modifierState.shiftKey = event.shiftKey;
      this.modifierState.altKey = event.altKey;
      this.modifierState.metaKey = event.metaKey;
    }
    
    // Normalize the key to handle accented characters
    let normalizedKey = event.key.toLowerCase();
    
    // Handle accented characters - normalize them to base characters
    const accentMap = {
      'ā': 'a', 'á': 'a', 'à': 'a', 'ä': 'a', 'â': 'a', 'ã': 'a',
      'ē': 'e', 'é': 'e', 'è': 'e', 'ë': 'e', 'ê': 'e',
      'ī': 'i', 'í': 'i', 'ì': 'i', 'ï': 'i', 'î': 'i',
      'ō': 'o', 'ó': 'o', 'ò': 'o', 'ö': 'o', 'ô': 'o', 'õ': 'o',
      'ū': 'u', 'ú': 'u', 'ù': 'u', 'ü': 'u', 'û': 'u',
      'ḍ': 'd', 'ď': 'd', 'đ': 'd',
      'ç': 'c', 'ñ': 'n'
    };
    
    // Replace accented characters with base characters
    if (accentMap[normalizedKey]) {
      normalizedKey = accentMap[normalizedKey];
    }
    
    // Create enhanced event with current modifier state
    const enhancedModifierState = {
      ctrlKey: this.modifierState.ctrlKey || event.ctrlKey,
      shiftKey: this.modifierState.shiftKey || event.shiftKey,
      altKey: this.modifierState.altKey || event.altKey,
      metaKey: this.modifierState.metaKey || event.metaKey
    };
    
    // Handle double shift detection first
    if (event.key === 'Shift') {
      this.doubleShiftDetector.handleKeyPress(event);
      return;
    }
    
    // Process regular shortcuts
    for (const shortcut of SHORTCUTS) {
      // Skip double-press shortcuts (handled separately)
      if (shortcut.special === 'double-press') {
        continue;
      }
      
      // Create a normalized event for comparison using tracked modifier state
      // Create a new object that preserves event methods while allowing property modification
      const normalizedEvent = {
        // Copy all enumerable properties
        ...event,
        // Override specific properties with normalized values
        key: normalizedKey,
        // Use tracked modifier state for more reliable detection
        ctrlKey: enhancedModifierState.ctrlKey,
        shiftKey: enhancedModifierState.shiftKey,
        altKey: enhancedModifierState.altKey,
        metaKey: enhancedModifierState.metaKey,
        // Preserve essential event methods by binding them to the original event
        preventDefault: event.preventDefault.bind(event),
        stopPropagation: event.stopPropagation.bind(event),
        stopImmediatePropagation: event.stopImmediatePropagation.bind(event),
        // Preserve other common event properties
        target: event.target,
        currentTarget: event.currentTarget,
        type: event.type,
        bubbles: event.bubbles,
        cancelable: event.cancelable,
        defaultPrevented: event.defaultPrevented
      };
      
      if (matchesShortcut(normalizedEvent, shortcut)) {
        const handler = HANDLER_MAP[shortcut.handler];
        if (handler) {
          handler(normalizedEvent, this.context);
        }
        break; // Only handle the first matching shortcut
      }
    }
  }
  
  /**
   * Get all available shortcuts for display
   */
  getShortcuts() {
    return SHORTCUTS;
  }
  
  /**
   * Cleanup resources
   */
  destroy() {
    this.stop();
    this.doubleShiftDetector.reset();
    this.context = {};
  }
}

/**
 * Singleton instance for global use
 */
export const keyboardManager = new KeyboardShortcutManager();